import { Box, Typography, ButtonBase } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCapsules,
  faPills,
  faSyringe,
  faDroplet,
  faClock,
  faPrescriptionBottlePill
} from "@fortawesome/pro-solid-svg-icons";
import {
  faPlus,
  faChevronDown,
  faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import { useTranslation } from "react-i18next";

const INFO_BORDER = "#8FC0FF";

function getRouteIcon(route) {
  if (!route) return faPrescriptionBottlePill;
  const r = route.toLowerCase();
  if (r.includes("intra") || r.includes("iv")) return faSyringe;
  if (
    r.includes("tóp") ||
    r.includes("top") ||
    r.includes("gota") ||
    r.includes("drop")
  )
    return faDroplet;
  return faPills;
}

function CollapsedHeader({ card, onExpand }) {
  const icon = getRouteIcon(card.route || card.dosage);
  return (
    <ButtonBase
      onClick={() => onExpand(true)}
      sx={{
        width: "100%",
        textAlign: "left",
        px: "24px",
        py: "16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        "&:hover": { opacity: 0.9 }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          flex: 1,
          minWidth: 0
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}
        >
          <FontAwesomeIcon
            icon={icon}
            style={{ color: semanticColors.text.primary, fontSize: 32 }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            minWidth: 0
          }}
        >
          <Typography
            sx={{
              ...typography.heading.xl,
              color: semanticColors.text.primary,
              lineHeight: 1.2
            }}
          >
            {card.name}
          </Typography>
          {(card.route || card.dosage) && (
            <Box
              sx={{
                display: "inline-flex",
                bgcolor: semanticColors.surface.containerLow,
                borderRadius: "8px",
                px: "4px",
                py: "2px",
                alignSelf: "flex-start"
              }}
            >
              <Typography
                sx={{
                  ...typography.body.lSemibold,
                  color: semanticColors.text.secondary
                }}
              >
                {card.route || card.dosage}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <FontAwesomeIcon
        icon={faPlus}
        style={{
          color: semanticColors.text.primary,
          fontSize: 20,
          flexShrink: 0
        }}
      />
    </ButtonBase>
  );
}

function DetailedHeader({ card, onExpand, isExpanded }) {
  const { t } = useTranslation();
  const hasPills = card.stockCount > 0;

  return (
    <ButtonBase
      onClick={() => onExpand(!isExpanded)}
      sx={{
        width: "100%",
        textAlign: "left",
        p: "24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        "&:hover": { opacity: 0.9 }
      }}
    >
      {/* Left: name, dosage, prescriber */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          flex: 1,
          minWidth: 0
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap"
          }}
        >
          <Typography
            sx={{ ...typography.heading.l, color: semanticColors.text.primary }}
          >
            {card.name}
          </Typography>
          {card.tradeName && (
            <Box
              sx={{
                bgcolor: semanticColors.surface.containerLow,
                borderRadius: "8px",
                px: "4px",
                py: "2px"
              }}
            >
              <Typography
                sx={{
                  ...typography.body.xlSemibold,
                  color: semanticColors.text.primary
                }}
              >
                {card.tradeName}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <Typography
            sx={{ ...typography.heading.s, color: semanticColors.text.primary }}
          >
            {card.dosage}
          </Typography>
          {card.quantityPerPackage && (
            <Typography
              sx={{ ...typography.body.l, color: semanticColors.text.primary }}
            >
              • {card.quantityPerPackage} {card.unit || "comprimidos"}
            </Typography>
          )}
        </Box>
        {card.prescriber && (
          <Typography
            sx={{ ...typography.body.m, color: semanticColors.text.primary }}
          >
            {t("planMedication.prescriber")}: <strong>{card.prescriber}</strong>
          </Typography>
        )}
      </Box>

      {/* Right: Próxima toma + pill badge + chevron */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexShrink: 0
        }}
      >
        {card.nextToma && (
          <Box
            sx={{
              bgcolor: semanticColors.feedback.infoBg,
              border: `1px solid ${INFO_BORDER}`,
              borderRadius: "14px",
              p: "8px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "2px"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <FontAwesomeIcon
                icon={faClock}
                style={{
                  color: semanticColors.status.processing,
                  fontSize: 20
                }}
              />
              <Typography
                sx={{
                  ...typography.body.l,
                  color: semanticColors.text.primary
                }}
              >
                {t("planMedication.nextDose")}
              </Typography>
            </Box>
            <Typography
              sx={{
                ...typography.heading.m,
                color: semanticColors.text.primary
              }}
            >
              {card.nextToma}
            </Typography>
          </Box>
        )}

        {card.stockCount !== undefined && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              bgcolor: hasPills
                ? semanticColors.highlight.low
                : "rgba(239,68,68,0.08)",
              border: `1px solid ${
                hasPills
                  ? semanticColors.highlight.default
                  : semanticColors.status.error
              }`,
              borderRadius: "14px",
              px: "12px",
              py: "2px",
              height: "30px"
            }}
          >
            <FontAwesomeIcon
              icon={faCapsules}
              style={{
                color: hasPills
                  ? semanticColors.highlight.default
                  : semanticColors.status.error,
                fontSize: 20
              }}
            />
            <Typography
              sx={{
                ...typography.heading.s,
                color: hasPills
                  ? semanticColors.highlight.default
                  : semanticColors.status.error
              }}
            >
              {card.stockCount}
            </Typography>
          </Box>
        )}

        <FontAwesomeIcon
          icon={isExpanded ? faChevronUp : faChevronDown}
          style={{ color: semanticColors.text.primary, fontSize: 16 }}
        />
      </Box>
    </ButtonBase>
  );
}

export default function CardHeader({
  card,
  onExpand,
  isExpanded,
  inGroup = false
}) {
  if (inGroup || isExpanded) {
    return (
      <DetailedHeader card={card} onExpand={onExpand} isExpanded={isExpanded} />
    );
  }
  return <CollapsedHeader card={card} onExpand={onExpand} />;
}
